import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Input,
  List,
  Avatar,
  Button,
  Spin,
  notification,
  Empty
} from 'antd';
import { productsApi } from '../../utils/api';
import { UserOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { getDeviseActive } from '../../utils/devise';

const { Search } = Input;

const ProductSelectionModal = ({ visible, onCancel, onSelect }) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]); // Contiendra la liste aplatie (produits + variants)

  // Fonction de recherche mémorisée avec useCallback
  const performSearch = useCallback(async (value) => {
    if (!value || value.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await productsApi.getAll({ search: value });
      const productsWithVariants = response?.data || [];
      console.log('productsWithVariants', productsWithVariants);
      
      const flatItems = [];
      productsWithVariants.forEach(product => {
        if (product.product_variants.length === 0) {
          flatItems.push({
            type: 'product',
            id: product.id,
            name: product.name,
            price: product.price,
            image: product.main_image,
            unit: product.unit,
            sku: product.sku,
            productId: product.id,
            stock: product.stock_level
          });
        } else {
          product.product_variants.forEach(variant => {
            flatItems.push({
              type: 'variant',
              id: variant.id,
              name: `${product.name.slice(0, 25)} - ${variant.title}`,
              variantTitle: variant.title,
              productName: product.name,
              price: variant.price,
              image: variant.image_url || product.main_image,
              unit: product.unit,
              sku: variant.sku,
              productId: product.id,
              stock: variant.inventory_quantity
            });
          });
        }
      });
      
      setResults(flatItems);
    } catch (error) {
      console.error('Erreur recherche produits/variants:', error);
      notification.error({ message: 'Erreur', description: 'Impossible de charger les produits.' });
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []); // Dépendances vides car productsApi est externe et les setters sont stables

  // Création de la fonction debounced mémorisée avec useMemo
  // Cela empêche la recréation de la fonction debounce à chaque rendu
  const debouncedSearch = useMemo(
    () => debounce(performSearch, 1000),
    [performSearch]
  );

  // Nettoyage du debounce au démontage
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Gérer le changement dans l'input de recherche
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value); // Met à jour immédiatement l'état pour l'affichage
    // Appelle la recherche avec debounce si l'utilisateur tape
    if (value.length === 0 || value.length >= 2) { 
        setLoading(true); // Active le spinner dès la saisie
        debouncedSearch(value); // Lance la recherche déboncée
    } else {
        setResults([]); // Vide les résultats si la saisie est trop courte
        setLoading(false); // Désactive le spinner
        debouncedSearch.cancel(); // Annule toute recherche déboncée en cours
    }
  };

  // Effacer les résultats quand le modal se ferme
  useEffect(() => {
    if (!visible) {
      setSearchTerm('');
      setResults([]);
    }
  }, [visible]);

  const handleSelect = (item) => {
    onSelect(item); // Appelle la fonction passée en prop avec l'item choisi
  };

  return (
    <Modal
      title="Sélectionner un Produit ou Variant"
      open={visible}
      onCancel={onCancel}
      width={600}
      footer={null} // Pas de footer par défaut, la sélection se fait dans la liste
    >
      <Search
        placeholder="Rechercher par nom, SKU..."
        loading={loading}
        style={{ marginBottom: 16 }}
        value={searchTerm}
        onChange={handleInputChange} // Utilisation de onChange pour la recherche dynamique
      />
      
      <Spin spinning={loading}>
        {results.length > 0 ? (
          <List
            itemLayout="horizontal"
            dataSource={results}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button type="primary" onClick={() => handleSelect(item)}>
                    Sélectionner
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={<Avatar src={item.image} icon={<UserOutlined />} size="large" />}
                  title={<span>{item.type === 'variant' ? item.productName : item.name}</span>} 
                  description={
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {item.type === 'variant' && (
                        <span style={{ fontWeight: 'bold' }}>{item.variantTitle}</span>
                      )}
                      <span>Prix: {item.price?.toFixed(2)} {getDeviseActive()}</span>
                      <span>SKU: {item.sku || 'N/A'}</span>
                      <span style={{ color: item.stock > 0 ? 'green' : 'red' }}>
                        Stock: {item.stock !== undefined && item.stock !== null ? item.stock : 'N/A'}
                      </span>
                    </div>
                  }
                />
              </List.Item>
            )}
            style={{ maxHeight: '400px', overflowY: 'auto' }}
          />
        ) : (
          <Empty description={loading ? 'Recherche...' : 'Aucun produit trouvé'} />
        )}
      </Spin>
    </Modal>
  );
};

export default ProductSelectionModal; 